import { ChevronDownIcon } from "@chakra-ui/icons";
import { Avatar, Box, Button, Flex, HStack, Menu, MenuButton, MenuItem, MenuList, Stack, Text } from "@chakra-ui/react";
import { shortenAddress, useEthers } from "@usedapp/core";
import BigNumber from "bignumber.js";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { I18N_NAMESPACE } from "../../../core/constants/i18n";
import { NFT_TYPE } from "../../../core/constants/nft";
import { IMoralisNft } from "../../../core/models/nft";
import { Quote } from "../../../core/models/quote";
import { IUser } from "../../../core/models/user";
import { formatNumber } from "../../../core/utils/number";
import { useAppSelector } from "../../../store/hook";

interface Props {
  owners: { user: IUser; quantityOwned: string }[];
  quotes: Quote[];
  selectedQuote: Quote | undefined;
  onPayFull: (quote: Quote) => void;
  onPayInFour: (quote: Quote) => void;
  nft: IMoralisNft | undefined;
}

export const OwnersTab = React.memo(({ owners, quotes, selectedQuote, onPayFull, onPayInFour, nft }: Props) => {
  const supportedTokens = useAppSelector((state) => state.token.supportedTokens);
  const { t } = useTranslation([I18N_NAMESPACE.ASSET_DETAIL]);
  const { account } = useEthers();
  const navigate = useNavigate();

  const onClickOwner = useCallback(
    (user: IUser) => () => {
      navigate(`/profile/assets/${user?.id}`);
    },
    []
  );

  return (
    <Stack spacing={5}>
      {owners.map((owner) => {
        const quote = quotes.find((i) => i.maker === owner.user.id);
        const token = quote?.take.asset_type.token_address
          ? supportedTokens.byId[quote?.take.asset_type.token_address]
          : null;
        const isCurrentUser = owner.user.id === account?.toLowerCase();
        return (
          <Flex key={owner?.user?.id}>
            <Box flex={1}>
              <HStack spacing={3} width={"fit-content"} onClick={onClickOwner(owner.user)} cursor={"pointer"}>
                <Avatar src={owner?.user.image || ""} size={"sm"} />
                <Box>
                  <Text fontSize={"sm"} fontWeight={500} _hover={{ color: "#000" }}>
                    {owner?.user.display_name || (owner?.user?.id && shortenAddress(owner.user.id))}
                  </Text>
                  {nft?.contract_type === NFT_TYPE.ERC_1155 && (
                    <Box>
                      {quote ? (
                        <Text fontSize={"xs"} color={"gray.500"}>
                          {quote.make.value}/{owner.quantityOwned} {t("on sale for")}{" "}
                          <Text as="span" color={"#000"}>
                            {token?.decimals
                              ? formatNumber(
                                  new BigNumber(quote.take.value || 0).div(Math.pow(10, token?.decimals)).toNumber()
                                )
                              : ""}{" "}
                            {token?.symbol}
                          </Text>{" "}
                          {t("all")}
                        </Text>
                      ) : (
                        <Text fontSize={"xs"} color={"gray.500"}>
                          {owner.quantityOwned} {t("editions")}{" "}
                          <Text as="span" color={"#000"}>
                            {t("not for sale")}
                          </Text>
                        </Text>
                      )}
                    </Box>
                  )}
                </Box>
              </HStack>
            </Box>

            {!isCurrentUser && !!quote && nft?.contract_type === NFT_TYPE.ERC_1155 && (
              <Box flex={1}>
                <Menu>
                  <MenuButton
                    as={Button}
                    colorScheme={"blue"}
                    disabled={quote.id === selectedQuote?.id}
                    size={"sm"}
                    rightIcon={<ChevronDownIcon />}
                  >
                    {t("Buy")}
                  </MenuButton>
                  <MenuList>
                    <MenuItem onClick={() => onPayInFour(quote)}>{t("Pay in 4")}</MenuItem>
                    <MenuItem onClick={() => onPayFull(quote)}>{t("Pay full")}</MenuItem>
                  </MenuList>
                </Menu>
              </Box>
            )}
          </Flex>
        );
      })}
    </Stack>
  );
});
